import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Play,
  Gauge,
  ListMusic,
  Moon,
  Bookmark,
  Smartphone,
  BookOpen,
  Highlighter,
  BookMarked,
  Search,
  Palette,
  SlidersHorizontal,
  DownloadCloud,
  Wifi,
  HardDrive,
  Trash2,
  ListOrdered,
  Plane,
  FolderOpen,
  Layers,
  UserPen,
  Filter,
  Clock,
  History,
  CalendarDays,
  Trophy,
  TrendingUp,
  CheckCircle,
  Car,
  Globe,
  ChevronLeft,
  ChevronRight,
  X,
  RefreshCw,
} from 'lucide-react';
import AppStoreBadges from '../components/AppStoreBadges';
import './Features.css';

// Category colors
const colors = {
  audio: '#346751',      // Sage green
  ebook: '#C9A962',      // Warm amber
  download: '#4A7C8C',   // Ocean blue
  library: '#8B6B8B',    // Soft plum
  stats: '#3D7A7A',      // Teal
  coming: '#C17F5E',     // Coral/terracotta
};

// Feature screenshots data - organized by section
const featureScreenshots = [
  // Index 0 - Audio section
  {
    src: '/images/screenshots/Screenshot_20251230_000618.webp',
    title: 'Book Details',
    description: 'Rich audiobook details with cover art, duration, genres, and quick actions',
  },
  // Index 1-4 - Ebook section
  {
    src: '/images/screenshots/Screenshot_20251230_000650.webp',
    title: 'eBook Integration',
    description: 'Seamlessly access companion eBooks alongside your audiobooks',
  },
  {
    src: '/images/screenshots/Screenshot_20251230_000838.webp',
    title: 'eBook Reader',
    description: 'Beautiful reading experience with customizable themes and highlighting',
  },
  {
    src: '/images/screenshots/Screenshot_20251230_000739.webp',
    title: 'Reader Settings',
    description: 'Customize your reading with themes, fonts, navigation, and font size controls',
  },
  {
    src: '/images/screenshots/Screenshot_20251230_000818.webp',
    title: 'Highlights & Notes',
    description: 'Mark important passages with multiple colors and add personal notes',
  },
  // Index 5 - Downloads section
  {
    src: '/images/screenshots/Screenshot_20251230_000927.webp',
    title: 'Smart Downloads',
    description: 'Intelligent download management with storage quotas and auto-cleanup',
  },
  // Index 6-7 - Library section
  {
    src: '/images/screenshots/Screenshot_20251230_000900.webp',
    title: 'Bookmarks & Highlights',
    description: 'Access all your saved bookmarks and highlights in one place',
  },
  {
    src: '/images/screenshots/Screenshot_20251230_001013.webp',
    title: 'Playlists',
    description: 'Create and manage playlists to organize your listening queue',
  },
  // Index 8 - Stats section
  {
    src: '/images/screenshots/Screenshot_20251230_000952.webp',
    title: 'Listening Stats',
    description: 'Track your listening time, streaks, sessions, and patterns',
  },
];

// Screenshot indices for each section
const sectionScreenshots = {
  audio: [0],
  ebook: [1, 2, 3, 4],
  download: [5],
  library: [6, 7],
  stats: [8],
};

function Features() {
  const observerRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % featureScreenshots.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + featureScreenshots.length) % featureScreenshots.length);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const audioFeatures = [
    { icon: <Play color={colors.audio} size={24} />, title: 'Playback Controls', description: 'Play, pause, skip forward/backward with customizable intervals (15s/30s default).' },
    { icon: <Gauge color={colors.audio} size={24} />, title: 'Variable Speed', description: 'Adjust playback speed from 0.5x to 3x. Find your perfect listening pace.' },
    { icon: <ListMusic color={colors.audio} size={24} />, title: 'Chapter Navigation', description: 'Jump between chapters, view chapter list, and see chapter-based progress.' },
    { icon: <Moon color={colors.audio} size={24} />, title: 'Sleep Timer', description: 'Set a timer for 5-60 minutes or until the end of the current chapter.' },
    { icon: <Bookmark color={colors.audio} size={24} />, title: 'Bookmarks', description: 'Save your place with custom notes. Bookmarks sync across devices; notes sync via iCloud on iOS.' },
    { icon: <Smartphone color={colors.audio} size={24} />, title: 'Lock Screen Controls', description: 'Full media controls from your lock screen and notification shade.' },
    { icon: <Car color={colors.audio} size={24} />, title: 'CarPlay & Android Auto', description: 'Control your audiobooks from your car\'s dashboard with full CarPlay integration. Android Auto coming soon.' },
  ];

  const ebookFeatures = [
    { icon: <BookOpen color={colors.ebook} size={24} />, title: 'EPUB & PDF Support', description: 'Read ebooks and PDFs with a professional-grade reader.' },
    { icon: <Highlighter color={colors.ebook} size={24} />, title: 'Highlights', description: 'Mark important passages with multiple colors. Add notes to your highlights. Syncs via iCloud on iOS.' },
    { icon: <BookMarked color={colors.ebook} size={24} />, title: 'Ebook Bookmarks', description: 'Save reading positions with custom titles. Syncs across all your devices.' },
    { icon: <Search color={colors.ebook} size={24} />, title: 'In-Book Search', description: 'Find any text within your current book instantly.' },
    { icon: <Palette color={colors.ebook} size={24} />, title: 'Reader Themes', description: 'Choose from light, dark, and sepia modes. Customize fonts and text size.' },
    { icon: <SlidersHorizontal color={colors.ebook} size={24} />, title: 'Reading Preferences', description: 'Customize font size, tap or swipe to turn pages, and choose paginated or continuous scroll.' },
    { icon: <RefreshCw color={colors.ebook} size={24} />, title: 'Progress Sync', description: 'Sync reading progress across devices via Audiobookshelf, or via iCloud on iOS.' },
  ];

  const downloadFeatures = [
    { icon: <DownloadCloud color={colors.download} size={24} />, title: 'Smart Downloads', description: 'Automatically download audiobooks you\'re currently listening to.' },
    { icon: <Wifi color={colors.download} size={24} />, title: 'WiFi-Only Mode', description: 'Save mobile data by restricting downloads to WiFi connections.' },
    { icon: <HardDrive color={colors.download} size={24} />, title: 'Storage Quotas', description: 'Set a maximum storage limit. The app manages space automatically.' },
    { icon: <Trash2 color={colors.download} size={24} />, title: 'Auto-Cleanup', description: 'Automatically remove finished or abandoned books to free up space.' },
    { icon: <ListOrdered color={colors.download} size={24} />, title: 'Queue Management', description: 'Pause, resume, and prioritize downloads. Full control over your queue.' },
    { icon: <Plane color={colors.download} size={24} />, title: 'Offline Playback', description: 'Listen to downloaded content anywhere, even without internet.' },
  ];

  const libraryFeatures = [
    { icon: <FolderOpen color={colors.library} size={24} />, title: 'Collections', description: 'Browse your collections and choose which ones appear on your home screen.' },
    { icon: <ListMusic color={colors.library} size={24} />, title: 'Playlists', description: 'Create playlists to queue up your next listens in order.' },
    { icon: <Layers color={colors.library} size={24} />, title: 'Series View', description: 'Browse books organized by series. Track your progress through each series.' },
    { icon: <UserPen color={colors.library} size={24} />, title: 'Author Pages', description: 'Explore all books by an author. View author biographies.' },
    { icon: <Search color={colors.library} size={24} />, title: 'Smart Search', description: 'Search across titles, authors, and narrators.' },
    { icon: <Filter color={colors.library} size={24} />, title: 'Filters & Sorting', description: 'Filter by progress, genre, tags, and ebook availability. Sort by title, date, or duration.' },
  ];

  const statsFeatures = [
    { icon: <Clock color={colors.stats} size={24} />, title: 'Listening Time', description: 'Track your total listening time across all audiobooks.' },
    { icon: <History color={colors.stats} size={24} />, title: 'Session History', description: 'Review your listening sessions with detailed breakdowns.' },
    { icon: <CalendarDays color={colors.stats} size={24} />, title: 'Time Periods', description: 'View stats for the last month, 6 months, year, or all time.' },
    { icon: <Trophy color={colors.stats} size={24} />, title: 'Milestones', description: 'Celebrate achievements like books finished and listening streaks.' },
    { icon: <TrendingUp color={colors.stats} size={24} />, title: 'Listening Patterns', description: 'Discover your favorite times to listen and reading habits.' },
    { icon: <CheckCircle color={colors.stats} size={24} />, title: 'Finished Books', description: 'Keep a record of all the books you\'ve completed.' },
  ];

  const platformFeatures = {
    ios: [
      { icon: <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>, title: 'SwiftUI', description: 'Built entirely in SwiftUI for smooth, responsive performance.' },
      { icon: <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>, title: 'Liquid Glass', description: 'Embraces the latest iOS 26 Liquid Glass design language.' },
    ],
    android: [
      { icon: <svg viewBox="0 0 24 24" fill="#3DDC84" width="24" height="24"><path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/></svg>, title: 'Jetpack Compose', description: 'Modern declarative UI for fast, fluid interactions.' },
      { icon: <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>, title: 'Material 3', description: 'Material Design 3 foundation with 11 handcrafted themes.' },
    ],
  };

  const howItWorks = [
    {
      step: 1,
      title: 'Set Up Audiobookshelf',
      description: <>Install Audiobookshelf on your server or NAS. Add your audiobook and ebook files to your library. <a href="https://www.audiobookshelf.org" target="_blank" rel="noopener noreferrer" className="feature-link">Get Audiobookshelf →</a></>,
    },
    {
      step: 2,
      title: 'Download Audra Books',
      description: 'Get the app from the App Store or Google Play. It\'s free to download with optional Pro features.',
    },
    {
      step: 3,
      title: 'Connect Your Server',
      description: 'Enter your server URL and log in with your credentials or SSO (OIDC). Your entire library will be available instantly.',
    },
    {
      step: 4,
      title: 'Start Listening',
      description: 'Browse your library, download for offline, and enjoy. Progress syncs across all your devices.',
    },
  ];

  return (
    <div className="features-page">
      <Helmet>
        <title>Features - Audra Books | Audiobook & Ebook App Features</title>
        <meta name="description" content="Explore all Audra Books features: audiobook playback, ebook reading, offline downloads, personal statistics, custom themes, and seamless sync across iOS and Android." />
        <link rel="canonical" href="https://audrabooks.app/features" />
        <meta property="og:title" content="Features - Audra Books" />
        <meta property="og:description" content="Explore all Audra Books features: audiobook playback, ebook reading, offline downloads, personal statistics, and more." />
        <meta property="og:url" content="https://audrabooks.app/features" />
      </Helmet>

      {/* Hero */}
      <section className="features-hero">
        <div className="container">
          <div className="features-hero-content animate-on-scroll">
            <h1>Every Feature You Need</h1>
            <p>
              Audra Books is packed with powerful features for audiobook listeners
              and ebook readers. Here's everything that makes it the premium
              Audiobookshelf experience.
            </p>
          </div>
        </div>
      </section>

      {/* Lightbox - rendered once, used by all sections */}
      {lightboxOpen && (
        <div className="screenshot-lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">
            <X size={24} />
          </button>
          <button
            className="lightbox-nav lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            aria-label="Previous"
          >
            <ChevronLeft size={32} />
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={featureScreenshots[lightboxIndex].src}
              alt={featureScreenshots[lightboxIndex].title}
            />
            <div className="lightbox-caption">
              <h3>{featureScreenshots[lightboxIndex].title}</h3>
              <p>{featureScreenshots[lightboxIndex].description}</p>
              <span className="lightbox-counter">
                {lightboxIndex + 1} / {featureScreenshots.length}
              </span>
            </div>
          </div>
          <button
            className="lightbox-nav lightbox-next"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            aria-label="Next"
          >
            <ChevronRight size={32} />
          </button>
        </div>
      )}

      {/* Audio Playback */}
      <section className="feature-section section">
        <div className="container">
          <div className="feature-section-header animate-on-scroll">
            <span className="section-badge">Audiobooks</span>
            <h2>Powerful Audio Playback</h2>
            <p>Professional-grade audio controls for the perfect listening experience.</p>
          </div>
          <div className="feature-section-with-screenshots">
            <div className="feature-grid">
              {audioFeatures.map((feature, index) => (
                <div key={index} className="feature-item animate-on-scroll" style={{ transitionDelay: `${index * 50}ms` }}>
                  <span className="feature-item-icon">{feature.icon}</span>
                  <div>
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="section-screenshots section-screenshots-single animate-on-scroll">
              {sectionScreenshots.audio.map((idx) => (
                <div key={idx} className="section-screenshot-card" onClick={() => openLightbox(idx)}>
                  <img src={featureScreenshots[idx].src} alt={featureScreenshots[idx].title} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ebook Reading */}
      <section className="feature-section section alt-bg">
        <div className="container">
          <div className="feature-section-header animate-on-scroll">
            <span className="section-badge">Ebooks</span>
            <h2>Beautiful Reading Experience</h2>
            <p>Read companion ebooks stored alongside your audiobooks—all from one unified library.</p>
          </div>
          <div className="feature-section-with-screenshots reverse">
            <div className="feature-grid">
              {ebookFeatures.map((feature, index) => (
                <div key={index} className="feature-item animate-on-scroll" style={{ transitionDelay: `${index * 50}ms` }}>
                  <span className="feature-item-icon">{feature.icon}</span>
                  <div>
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="section-screenshots section-screenshots-multi animate-on-scroll">
              {sectionScreenshots.ebook.map((idx) => (
                <div key={idx} className="section-screenshot-card" onClick={() => openLightbox(idx)}>
                  <img src={featureScreenshots[idx].src} alt={featureScreenshots[idx].title} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Downloads */}
      <section className="feature-section section">
        <div className="container">
          <div className="feature-section-header animate-on-scroll">
            <span className="section-badge">Downloads</span>
            <h2>Smart Download System</h2>
            <p>Intelligent offline management that just works.</p>
          </div>
          <div className="feature-section-with-screenshots">
            <div className="feature-grid">
              {downloadFeatures.map((feature, index) => (
                <div key={index} className="feature-item animate-on-scroll" style={{ transitionDelay: `${index * 50}ms` }}>
                  <span className="feature-item-icon">{feature.icon}</span>
                  <div>
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="section-screenshots section-screenshots-single animate-on-scroll">
              {sectionScreenshots.download.map((idx) => (
                <div key={idx} className="section-screenshot-card" onClick={() => openLightbox(idx)}>
                  <img src={featureScreenshots[idx].src} alt={featureScreenshots[idx].title} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Library */}
      <section className="feature-section section alt-bg">
        <div className="container">
          <div className="feature-section-header animate-on-scroll">
            <span className="section-badge">Library</span>
            <h2>Organize Your Collection</h2>
            <p>Powerful tools to browse, search, and manage your library.</p>
          </div>
          <div className="feature-section-with-screenshots reverse">
            <div className="feature-grid">
              {libraryFeatures.map((feature, index) => (
                <div key={index} className="feature-item animate-on-scroll" style={{ transitionDelay: `${index * 50}ms` }}>
                  <span className="feature-item-icon">{feature.icon}</span>
                  <div>
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="section-screenshots section-screenshots-double animate-on-scroll">
              {sectionScreenshots.library.map((idx) => (
                <div key={idx} className="section-screenshot-card" onClick={() => openLightbox(idx)}>
                  <img src={featureScreenshots[idx].src} alt={featureScreenshots[idx].title} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="feature-section section">
        <div className="container">
          <div className="feature-section-header animate-on-scroll">
            <span className="section-badge">Statistics</span>
            <h2>Track Your Progress</h2>
            <p>Detailed insights into your listening habits and achievements.</p>
          </div>
          <div className="feature-section-with-screenshots">
            <div className="feature-grid">
              {statsFeatures.map((feature, index) => (
                <div key={index} className="feature-item animate-on-scroll" style={{ transitionDelay: `${index * 50}ms` }}>
                  <span className="feature-item-icon">{feature.icon}</span>
                  <div>
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="section-screenshots section-screenshots-single animate-on-scroll">
              {sectionScreenshots.stats.map((idx) => (
                <div key={idx} className="section-screenshot-card" onClick={() => openLightbox(idx)}>
                  <img src={featureScreenshots[idx].src} alt={featureScreenshots[idx].title} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Platform-Specific */}
      <section className="feature-section section alt-bg">
        <div className="container">
          <div className="feature-section-header animate-on-scroll">
            <span className="section-badge">Platform</span>
            <h2>Native on iOS & Android</h2>
            <p>Same features, native feel on both platforms.</p>
          </div>
          <div className="platform-comparison">
            <div className="platform-column animate-on-scroll">
              <div className="platform-header ios">
                <span className="platform-icon-large">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                </span>
                <h3>iOS & iPadOS</h3>
              </div>
              <div className="platform-features">
                {platformFeatures.ios.map((feature, index) => (
                  <div key={index} className="platform-feature-item">
                    <span>{feature.icon}</span>
                    <div>
                      <strong>{feature.title}</strong>
                      <p>{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="platform-column animate-on-scroll" style={{ transitionDelay: '150ms' }}>
              <div className="platform-header android">
                <span className="platform-icon-large">
                  <svg viewBox="0 0 24 24" fill="#3DDC84" width="48" height="48"><path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/></svg>
                </span>
                <h3>Android</h3>
              </div>
              <div className="platform-features">
                {platformFeatures.android.map((feature, index) => (
                  <div key={index} className="platform-feature-item">
                    <span>{feature.icon}</span>
                    <div>
                      <strong>{feature.title}</strong>
                      <p>{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works section alt-bg">
        <div className="container">
          <div className="feature-section-header animate-on-scroll">
            <span className="section-badge">Getting Started</span>
            <h2>How It Works</h2>
          </div>
          <div className="steps-container">
            {howItWorks.map((step, index) => (
              <div key={index} className="step-item animate-on-scroll" style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="coming-soon section">
        <div className="container">
          <div className="feature-section-header animate-on-scroll">
            <span className="section-badge">Roadmap</span>
            <h2>Coming Soon</h2>
            <p>Features we're working on for future releases.</p>
          </div>
          <div className="coming-soon-grid">
            <div className="coming-soon-item animate-on-scroll">
              <div className="coming-soon-icon"><Car color={colors.coming} size={32} /></div>
              <h4>Android Auto</h4>
              <p>Seamless Android Auto support for safe, hands-free listening on the road.</p>
            </div>
            <div className="coming-soon-item animate-on-scroll" style={{ transitionDelay: '100ms' }}>
              <div className="coming-soon-icon"><Globe color={colors.coming} size={32} /></div>
              <h4>Localization</h4>
              <p>Full language support to bring Audra Books to listeners around the world.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Promotion */}
      <section className="cross-promo-section">
        <div className="container">
          <Link to="/shelfpulse" className="cross-promo-card animate-on-scroll">
            <div className="cross-promo-badge">For Server Owners</div>
            <div className="cross-promo-content">
              <div className="cross-promo-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 7h8M8 11h8M8 15h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="cross-promo-text">
                <strong>ShelfPulse</strong>
                <span>Monitor your Audiobookshelf server with real-time sessions & analytics</span>
              </div>
              <div className="cross-promo-arrow">→</div>
            </div>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="features-cta section">
        <div className="container">
          <div className="features-cta-content animate-on-scroll">
            <h2>Ready to Get Started?</h2>
            <p>Download Audra Books today and experience your audiobook library like never before.</p>
            <AppStoreBadges size="large" />
            <div className="cta-note">
              Free to download. Requires an{' '}
              <a href="https://www.audiobookshelf.org" target="_blank" rel="noopener noreferrer">
                Audiobookshelf
              </a>{' '}
              server.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Features;
